import React, { useState } from 'react';
import { ShieldCheck, Check, AlertTriangle, Clock, DollarSign, Calendar, ChevronDown, ChevronUp, MapPin, CheckCircle } from 'lucide-react';
import { SafetyRecommendation, RiskZone } from './types';

interface RecommendationsTabProps {
  recommendations: SafetyRecommendation[];
  onImplementRecommendation: (recommendationId: string) => void;
  riskZones: RiskZone[];
}

const RecommendationsTab: React.FC<RecommendationsTabProps> = ({ 
  recommendations, 
  onImplementRecommendation,
  riskZones
}) => {
  // State for expanded recommendations
  const [expandedRecommendations, setExpandedRecommendations] = useState<string[]>([]);
  const [filterBy, setFilterBy] = useState<{
    status: ('PENDING' | 'IN_PROGRESS' | 'IMPLEMENTED' | 'DISMISSED')[];
    priority: ('HIGH' | 'MEDIUM' | 'LOW')[];
    category: ('ENVIRONMENTAL' | 'BEHAVIORAL' | 'EQUIPMENT' | 'TRAINING' | 'MEDICAL')[];
  }>({
    status: [],
    priority: [],
    category: []
  });
  
  // Toggle recommendation expansion
  const toggleExpand = (id: string) => {
    setExpandedRecommendations(prev => 
      prev.includes(id) 
        ? prev.filter(recId => recId !== id) 
        : [...prev, id]
    );
  };
  
  // Filter toggle handlers
  const toggleStatusFilter = (status: 'PENDING' | 'IN_PROGRESS' | 'IMPLEMENTED' | 'DISMISSED') => {
    setFilterBy(prev => {
      const statuses = [...prev.status];
      if (statuses.includes(status)) {
        return { ...prev, status: statuses.filter(s => s !== status) };
      } else {
        return { ...prev, status: [...statuses, status] };
      }
    });
  };
  
  const togglePriorityFilter = (priority: 'HIGH' | 'MEDIUM' | 'LOW') => {
    setFilterBy(prev => {
      const priorities = [...prev.priority];
      if (priorities.includes(priority)) {
        return { ...prev, priority: priorities.filter(p => p !== priority) };
      } else {
        return { ...prev, priority: [...priorities, priority] };
      }
    });
  };
  
  const toggleCategoryFilter = (category: 'ENVIRONMENTAL' | 'BEHAVIORAL' | 'EQUIPMENT' | 'TRAINING' | 'MEDICAL') => {
    setFilterBy(prev => {
      const categories = [...prev.category];
      if (categories.includes(category)) {
        return { ...prev, category: categories.filter(c => c !== category) };
      } else {
        return { ...prev, category: [...categories, category] };
      }
    });
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilterBy({
      status: [],
      priority: [],
      category: []
    });
  };
  
  // Filter recommendations
  const filteredRecommendations = recommendations.filter(rec => {
    if (filterBy.status.length > 0 && !filterBy.status.includes(rec.status)) {
      return false;
    }
    
    if (filterBy.priority.length > 0 && !filterBy.priority.includes(rec.priority)) {
      return false;
    }
    
    if (filterBy.category.length > 0 && !filterBy.category.includes(rec.category)) {
      return false;
    }
    
    return true;
  });
  
  // Sort recommendations by priority and status
  const sortedRecommendations = [...filteredRecommendations].sort((a, b) => {
    // Sort by priority first (HIGH > MEDIUM > LOW)
    const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
    
    if (priorityDiff !== 0) return priorityDiff;
    
    // Then sort by status (PENDING > IN_PROGRESS > IMPLEMENTED > DISMISSED)
    const statusOrder = { PENDING: 4, IN_PROGRESS: 3, IMPLEMENTED: 2, DISMISSED: 1 };
    return statusOrder[b.status] - statusOrder[a.status];
  });
  
  // Get badge color for priority
  const getPriorityBadge = (priority: 'HIGH' | 'MEDIUM' | 'LOW') => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get badge color for status
  const getStatusBadge = (status: 'PENDING' | 'IN_PROGRESS' | 'IMPLEMENTED' | 'DISMISSED') => {
    switch (status) {
      case 'PENDING':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
        return 'bg-purple-100 text-purple-800';
      case 'IMPLEMENTED':
        return 'bg-green-100 text-green-800';
      case 'DISMISSED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get category badge color
  const getCategoryBadge = (category: 'ENVIRONMENTAL' | 'BEHAVIORAL' | 'EQUIPMENT' | 'TRAINING' | 'MEDICAL') => {
    switch (category) {
      case 'ENVIRONMENTAL':
        return 'bg-blue-100 text-blue-800';
      case 'BEHAVIORAL':
        return 'bg-purple-100 text-purple-800';
      case 'EQUIPMENT':
        return 'bg-yellow-100 text-yellow-800';
      case 'TRAINING':
        return 'bg-green-100 text-green-800';
      case 'MEDICAL':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Format date
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Get room name for risk zone
  const getRoomForRiskZone = (zoneId: string): string => {
    const zone = riskZones.find(z => z.id === zoneId);
    return zone ? zone.location.room : 'Unknown';
  };
  
  // Calculate recommendation stats
  const pendingRecommendations = recommendations.filter(r => r.status === 'PENDING').length;
  const inProgressRecommendations = recommendations.filter(r => r.status === 'IN_PROGRESS').length;
  const implementedRecommendations = recommendations.filter(r => r.status === 'IMPLEMENTED').length;
  const highPriorityPending = recommendations.filter(r => r.status === 'PENDING' && r.priority === 'HIGH').length;
  
  return (
    <div>
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Pending</h3>
            <Clock className="text-blue-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{pendingRecommendations}</p>
          <p className="text-xs text-gray-500">Need attention</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">In Progress</h3>
            <Clock className="text-purple-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{inProgressRecommendations}</p>
          <p className="text-xs text-gray-500">Being implemented</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Implemented</h3>
            <CheckCircle className="text-green-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{implementedRecommendations}</p>
          <p className="text-xs text-gray-500">Complete</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">High Priority</h3>
            <AlertTriangle className="text-red-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{highPriorityPending}</p>
          <p className="text-xs text-gray-500">Needs urgent attention</p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Recommendations</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filters */}
          <div>
            <p className="text-xs text-gray-500 mb-2">Status</p>
            <div className="flex flex-wrap gap-2">
              {(['PENDING', 'IN_PROGRESS', 'IMPLEMENTED', 'DISMISSED'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => toggleStatusFilter(status)}
                  className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium 
                    ${filterBy.status.includes(status) ? getStatusBadge(status) : 'bg-gray-100 text-gray-700'}`}
                >
                  {filterBy.status.includes(status) && <Check className="h-3 w-3 mr-1" />}
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
          
          {/* Priority Filters */}
          <div>
            <p className="text-xs text-gray-500 mb-2">Priority</p>
            <div className="flex flex-wrap gap-2">
              {(['HIGH', 'MEDIUM', 'LOW'] as const).map(priority => (
                <button
                  key={priority}
                  onClick={() => togglePriorityFilter(priority)}
                  className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium 
                    ${filterBy.priority.includes(priority) ? getPriorityBadge(priority) : 'bg-gray-100 text-gray-700'}`}
                >
                  {filterBy.priority.includes(priority) && <Check className="h-3 w-3 mr-1" />}
                  {priority}
                </button>
              ))}
            </div>
          </div>
          
          {/* Category Filters */}
          <div>
            <p className="text-xs text-gray-500 mb-2">Category</p>
            <div className="flex flex-wrap gap-2">
              {(['ENVIRONMENTAL', 'BEHAVIORAL', 'EQUIPMENT', 'TRAINING', 'MEDICAL'] as const).map(category => (
                <button
                  key={category}
                  onClick={() => toggleCategoryFilter(category)}
                  className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium 
                    ${filterBy.category.includes(category) ? getCategoryBadge(category) : 'bg-gray-100 text-gray-700'}`}
                >
                  {filterBy.category.includes(category) && <Check className="h-3 w-3 mr-1" />}
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Clear Filters */}
        {(filterBy.status.length > 0 || filterBy.priority.length > 0 || filterBy.category.length > 0) && (
          <div className="flex justify-end mt-3">
            <button
              onClick={clearFilters}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      
      {/* Recommendations List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Safety Recommendations</h3>
          <p className="text-sm text-gray-500">Personalized recommendations to improve safety</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {sortedRecommendations.length > 0 ? (
            sortedRecommendations.map(recommendation => (
              <div key={recommendation.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <ShieldCheck 
                      className={`
                        mt-1 mr-3 flex-shrink-0
                        ${recommendation.priority === 'HIGH' ? 'text-red-500' : 
                          recommendation.priority === 'MEDIUM' ? 'text-yellow-500' : 
                          'text-green-500'}
                      `} 
                      size={20} 
                    />
                    <div>
                      <div className="flex items-center flex-wrap gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(recommendation.priority)}`}>
                          {recommendation.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(recommendation.status)}`}>
                          {recommendation.status.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryBadge(recommendation.category)}`}>
                          {recommendation.category}
                        </span>
                      </div>
                      <h4 className="text-base font-medium mt-1">{recommendation.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{recommendation.description}</p>
                      
                      {/* Target Locations */}
                      {recommendation.targetRiskZones && recommendation.targetRiskZones.length > 0 && (
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          <MapPin size={12} className="mr-1" />
                          <span>
                            Target Areas: {recommendation.targetRiskZones.map(zoneId => getRoomForRiskZone(zoneId)).join(', ')}
                          </span>
                        </div>
                      )}
                      
                      {/* Basic Metadata */}
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <Calendar size={12} className="mr-1" />
                        <span>Created: {formatDate(recommendation.createdAt)}</span>
                        {recommendation.implementedAt && (
                          <>
                            <span className="mx-2">•</span>
                            <CheckCircle size={12} className="mr-1 text-green-500" />
                            <span>Implemented: {formatDate(recommendation.implementedAt)}</span>
                          </>
                        )}
                      </div>
                      
                      {/* View Details / Hide Details toggle */}
                      <button
                        onClick={() => toggleExpand(recommendation.id)}
                        className="mt-2 flex items-center text-blue-600 text-xs"
                      >
                        {expandedRecommendations.includes(recommendation.id) ? (
                          <>
                            <ChevronUp size={14} className="mr-1" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} className="mr-1" />
                            View Details
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <div className="ml-4 flex-shrink-0">
                    {recommendation.status === 'PENDING' && (
                      <button
                        onClick={() => onImplementRecommendation(recommendation.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Implement
                      </button>
                    )}
                    {recommendation.status === 'IN_PROGRESS' && (
                      <button
                        onClick={() => onImplementRecommendation(recommendation.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200"
                      >
                        Mark Complete
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedRecommendations.includes(recommendation.id) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Implementation Steps */}
                      {recommendation.detailedSteps && recommendation.detailedSteps.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Implementation Steps</h5>
                          <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                            {recommendation.detailedSteps.map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      
                      {/* Additional Details */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Additional Details</h5>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <dt className="text-gray-500">Expected Impact</dt>
                          <dd className="text-gray-900">{recommendation.impact}% risk reduction</dd>
                          
                          {recommendation.estimatedCost && (
                            <>
                              <dt className="text-gray-500">Estimated Cost</dt>
                              <dd className="text-gray-900 flex items-center">
                                <DollarSign size={14} className="mr-1" />
                                {recommendation.estimatedCost}
                              </dd>
                            </>
                          )}
                          
                          {recommendation.estimatedTimeToImplement && (
                            <>
                              <dt className="text-gray-500">Est. Implementation Time</dt>
                              <dd className="text-gray-900 flex items-center">
                                <Clock size={14} className="mr-1" />
                                {recommendation.estimatedTimeToImplement}
                              </dd>
                            </>
                          )}
                          
                          {recommendation.assignedTo && (
                            <>
                              <dt className="text-gray-500">Assigned To</dt>
                              <dd className="text-gray-900">{recommendation.assignedTo}</dd>
                            </>
                          )}
                        </dl>
                        
                        {/* Progress Bar for IN_PROGRESS items */}
                        {recommendation.status === 'IN_PROGRESS' && (
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-700">Implementation Progress</span>
                              <span className="text-xs text-gray-500">50%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '50%' }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <ShieldCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations found</h3>
              <p className="text-gray-500">
                {filterBy.status.length > 0 || filterBy.priority.length > 0 || filterBy.category.length > 0
                  ? 'Try adjusting your filter criteria'
                  : 'No safety recommendations available at this time'}
              </p>
              {(filterBy.status.length > 0 || filterBy.priority.length > 0 || filterBy.category.length > 0) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationsTab; 